import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup, FormBuilder, Validators, AbstractControl, FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { PaymentService, AuthService, ErrorHandlerService, HelperDialogModel, HelperDialogComponent } from 'epay-library';
import { IPaymentAccount } from 'epay-library/lib/features/common/models/payment/paymentaccount';
import { Location } from '@angular/common';

@Component({
  selector: 'epay-common-employer-payment-account',
  templateUrl: './epay-common-employer-payment-account.component.html'
})
export class EpayCommonEmployerPaymentAccountComponent implements OnInit {
  @Input() baseurl = '';
  @Input() editaccount: IPaymentAccount = {} as IPaymentAccount;
  @Input() routingHelpImage = '';
  @Output() closeAccountPanel = new EventEmitter<{
    show: boolean;
    success: boolean;
    message: string;
    confirmationNumber: string;
  }>();

  isLoggedIn = false;
  bankaccountFrmGp: FormGroup = new FormGroup({});
  accountKey = '';
  isEdit = false;
  title = '';
  showDialogHelp = false;
  submitted = false;
  accountHasHold = false;

  routingHide = true;
  routingConfirmHide = true;
  accountHide = true;
  accountConfirmHide = true;

  errorMatcher = new CrossFieldErrorMatcher();

  constructor(
    private formbuilder: FormBuilder,
    private location: Location,
    private paymentService: PaymentService,
    private authservice: AuthService,
    private errorHandlerService: ErrorHandlerService,
    private router: Router,
    public dialog: MatDialog,
    public snackbar: MatSnackBar
  ) {
    this.isEdit = false;
    this.accountKey = '';
  }

  ngOnInit() {
    this.isEdit = false;
    this.loadformGroups();
    const routing2Ctrl = this.bankaccountFrmGp.get('routingGrp.routing2')!;
    if (
      this.editaccount.accountNumberMasked === null ||
      this.editaccount.accountNumberMasked === undefined
    ) {
      this.isEdit = false;
      this.title = 'Setup New Bank Account';
      this.accountKey = '';
    } else {
      this.loadEditAccount();
    }

    this.bankaccountFrmGp
      .get('routingGrp')!
      .valueChanges.subscribe((change) => (this.accountHasHold = false));

    this.bankaccountFrmGp
      .get('accountGrp')!
      .valueChanges.subscribe((change) => (this.accountHasHold = false));
  }

  loadformGroups() {
    this.bankaccountFrmGp = this.formbuilder.group({
      bankName: ['', [Validators.required, Validators.maxLength(25)]],
      accountType: ['', Validators.required],
      bankphone: '',
      routingGrp: this.formbuilder.group(
        {
          routing1: ['', [Validators.required, abaValidation]],
          routing2: ['', Validators.required],
        },
        { validators: routingMatcher }
      ),
      accountGrp: this.formbuilder.group(
        {
          account1: ['', Validators.required],
          account2: ['', Validators.required],
        },
        { validators: accountMatcher }
      ),
      bankNickName: '',
    });
  }

  loadEditAccount() {
    this.isEdit = true;
    this.accountKey = this.editaccount.scheduledPaymentAccountKey;
    this.bankaccountFrmGp.patchValue({
      bankName: this.editaccount.bankName,
      accountType: this.editaccount.accountType,
      bankphone: this.editaccount.bankPhone,
      routingGrp: {
        routing1: this.editaccount.abaRouting,
        routing2: this.editaccount.abaRouting,
      },
      accountGrp: {
        account1: this.editaccount.accountNumberMasked,
        account2: this.editaccount.accountNumberMasked,
      },
    });

    this.title =
      'Edit Payment Account (' + this.editaccount.accountNumberMasked + ')';
    this.bankaccountFrmGp.disable();
    this.bankaccountFrmGp.get('bankName')!.enable();
    this.bankaccountFrmGp.get('bankphone')!.enable();
  }

  closePanel() {
    this.bankaccountFrmGp.reset();
    this.isEdit = false;
    this.accountHasHold = false;
    this.closeAccountPanel.emit({
      show: false,
      success: false,
      message: '',
      confirmationNumber: '',
    });
  }

  insertUpdateAccount() {
    this.submitted = true;
    this.accountHasHold = false;
    if (this.bankaccountFrmGp.valid) {
      if (this.bankaccountFrmGp.dirty) {
        const account = {
          account: this.bankaccountFrmGp.get('accountGrp.account1')!.value,
          abaRouting: this.bankaccountFrmGp.get('routingGrp.routing1')!.value,
          scheduledPaymentAccountKey: this.isEdit ? this.accountKey : '',
          payerType: 'EMP',
          accountType: this.bankaccountFrmGp.get('accountType')!.value,
          accountNickName: '',
          bankName: this.bankaccountFrmGp.get('bankName')!.value,
          bankPhone: this.bankaccountFrmGp.get('bankphone')!.value,
        };
        this.paymentService
          .insertUpdateEMPBankAccount(this.baseurl, account)
          .subscribe({
            next: (response: any) => {
              const comm = this.isEdit
                ? 'Your bank account has been updated!'
                : 'New bank account has been created!';
              this.bankaccountFrmGp.reset();
              this.isEdit = false;
              this.closeAccountPanel.emit({
                show: false,
                success: true,
                message: comm,
                confirmationNumber: response.confirmationNumber,
              });
            },
            error: (error: any) => {
              // 400 ERROR means account is on hold
              if (error.status === 400) {
                this.accountHasHold = true;
              } else {
                this.logError(error);
                this.handleError(error.status);
              }
            },
          });
      }
    }
  }

  openSnackBar(message: string, time: number, style: string) {
    const config = new MatSnackBarConfig();
    config.horizontalPosition = 'center';
    config.verticalPosition = 'top';
    config.duration = time;
    config.panelClass = style;
    this.snackbar.open(message, '', config);
  }

  toggleRoutingHide() {
    this.routingHide = !this.routingHide;
  }

  toggleRoutingConfirmHide() {
    this.routingConfirmHide = !this.routingConfirmHide;
  }

  toggleAccountHide() {
    this.accountHide = !this.accountHide;
  }

  toggleAccountConfirmHide() {
    this.accountConfirmHide = !this.accountConfirmHide;
  }

  displayRoutingHelp() {
    const message = 'See image below for help.';
    const imagePath = this.routingHelpImage;
    const dialogData = new HelperDialogModel(
      'Bank Account Help',
      message,
      imagePath
    );

    const dialogRef = this.dialog.open(HelperDialogComponent, {
      maxWidth: '1000px',
      data: dialogData,
    });

    dialogRef
      .afterClosed()
      .subscribe((dialogResult) => (this.showDialogHelp = false));
  }

  logError(error: Error) {
    this.errorHandlerService.logError(error);
  }

  handleError(error: number) {
    this.errorHandlerService.handleErrorMessage(error, this.baseurl);
  }
}

function abaValidation(
  control: AbstractControl
): { [key: string]: boolean } | null {
  const routing = control.value;
  if (abaRoutingNumberIsValid(routing)) {
    return null;
  }
  return { abaInvalid: true };
}

function routingMatcher(c: AbstractControl): { [key: string]: boolean } | null {
  const routing1Ctrl = c.get('routing1');
  const routing2Ctrl = c.get('routing2');
  if (routing1Ctrl === null || routing2Ctrl === null) {
    return null;
  }
  if (routing1Ctrl.pristine || routing2Ctrl.pristine) {
    return null;
  }
  if (routing1Ctrl.value === routing2Ctrl.value) {
    return null;
  }
  return { doNotMatch: true };
}

function accountMatcher(c: AbstractControl): { [key: string]: boolean } | null {
  const account1Ctrl = c.get('account1');
  const account2Ctrl = c.get('account2');
  if (account1Ctrl === null || account2Ctrl === null) {
    return null;
  }
  if (account1Ctrl.pristine || account2Ctrl.pristine) {
    return null;
  }
  if (account1Ctrl.value === account2Ctrl.value) {
    return null;
  }
  return { doNotMatch: true };
}

/** Error when the parent is invalid */
class CrossFieldErrorMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null
  ): boolean {
    if (control) {
      return (control.dirty || control.touched) && control.parent!.invalid;
    }
    return false;
  }
}

function abaRoutingNumberIsValid(routingNumberToTest: any) {
  if (!routingNumberToTest) {
    //all 0's is technically a valid routing number, but it's inactive
    return false;
  }

  var routing = routingNumberToTest.toString();

  //gotta be 9  digits
  var match = routing.match('^\\d{9}$');
  if (!match) {
    return false;
  }

  //The first two digits of the nine digit RTN must be in the ranges 00 through 12, 21 through 32, 61 through 72, or 80.
  const firstTwo = parseInt(routing.substring(0, 2));
  const firstTwoValid =
    (0 <= firstTwo && firstTwo <= 12) ||
    (21 <= firstTwo && firstTwo <= 32) ||
    (61 <= firstTwo && firstTwo <= 72) ||
    firstTwo === 80;
  if (!firstTwoValid) {
    return false;
  }

  //this is the checksum
  const weights = [3, 7, 1];
  var sum = 0;
  for (var i = 0; i < 8; i++) {
    sum += parseInt(routing[i]) * weights[i % 3];
  }

  return (10 - (sum % 10)) % 10 === parseInt(routing[8]);
}

